import { PaymentFlowContext, PaymentFlowResult } from '@/Services/PaymentMethod/types';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import AppConfig from '@/AppConfig';
import { PaymentIntent, PaymentMethod } from '@stripe/stripe-js';
import Authenticator from '@/Services/Auth/Authenticator';
import {
  confirmSetup,
  lockUserAccount,
  processSetupIntentConfirmation,
  redirectToPaymentComplete,
} from '@/Services/PaymentMethod/processPayment';
import { mapTaxLocationValidationError } from '@/Services/PaymentMethod/taxLocationError';

export default async function processCardPayment(context: PaymentFlowContext): Promise<PaymentFlowResult> {
  const stripeSubService = context.stripeSubscriptionService;
  const isUpgrade = !!stripeSubService.subscription?.id;
  const isDowngrade = context.checkoutType === 'downgrade';

  try {
    // if purchase is a downgrade, we won't have a payment intent to confirm, so
    // we just confirm setup intent and then go to the success page
    if (isDowngrade) {
      const { error, setupIntent } = await confirmSetup(context);
      return await processSetupIntentConfirmation(context, setupIntent, error, 'card');
    }

    const { clientSecret } = await createOrUpdateCustomerAndSubscription(context, 'card', isUpgrade);

    if (clientSecret) {
      const { error, intent } = await confirmPaymentIntent(context, clientSecret);
      return await processPaymentIntentConfirmation(context, intent, error);
    }
    // if this is not an initial purchase, no additional authentication is needed,
    // and the payment intent isn't pending, no client secret is returned.
    await redirectToPaymentComplete(context);

    return { success: true };
  } catch (error: any) {
    // Bad billing address rejected by Stripe Tax validation — a user-fixable 422, not an exception.
    const taxLocationError = mapTaxLocationValidationError(error);
    if (taxLocationError) {
      return { success: false, error: taxLocationError };
    }

    Sentry.captureException(error);
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-102',
      },
    };
  }
}

async function createOrUpdateCustomerAndSubscription(
  context: PaymentFlowContext,
  paymentMethodType: string,
  isUpgrade: boolean
): Promise<{ clientSecret?: string }> {
  if (isUpgrade) {
    return await upgradeSubscription(context, paymentMethodType);
  } else {
    return await createInitialCustomerAndSubscription(context, paymentMethodType);
  }
}

async function createInitialCustomerAndSubscription(
  context: PaymentFlowContext,
  paymentMethodType: string
): Promise<{ clientSecret?: string }> {
  // Step 1: Create customer and save billing details
  // Call payment-methods endpoint WITHOUT new_payment_method_id to create customer
  await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods', {
    legal_entity: context.stripeCheckoutService.legalEntity,
    customer: context.stripeCheckoutService.customer,
  });

  // Step 2: Create subscription - this returns a client_secret for Stripe confirmation
  const response = await axios.post(AppConfig.getAPIBaseUrl() + '/subscription/stripe/', {
    plan_id: context.selectedPlan.id,
    payment_method_type: paymentMethodType,
    is_wallet: false,
  });

  return {
    clientSecret: response.data.client_secret,
  };
}

async function upgradeSubscription(
  context: PaymentFlowContext,
  paymentMethodType: string
): Promise<{ clientSecret?: string }> {
  // Upgrade subscription - this returns a client_secret for Stripe confirmation
  // The payment method will be created automatically during confirmPayment
  const response = await axios.patch(AppConfig.getAPIBaseUrl() + '/subscription/stripe/' + context.selectedPlan.id, {
    plan_id: context.selectedPlan.id,
    payment_method_type: paymentMethodType,
    is_wallet: false,
  });

  return {
    clientSecret: response.data.client_secret,
  };
}

async function confirmPaymentIntent(
  context: PaymentFlowContext,
  clientSecret: string
): Promise<{ error: any; intent: PaymentIntent | undefined }> {
  if (!context.stripe || !context.elements) {
    return { error: { message: 'not_ready' }, intent: undefined };
  }

  try {
    const billingAddress = context.stripeCheckoutService.customer?.address;

    const { error, paymentIntent } = await context.stripe.confirmPayment({
      elements: context.elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/processing?returningFromPaymentElements=true`,
        payment_method_data: {
          billing_details: {
            name: context.stripeCheckoutService.customer?.name,
            email: context.stripeCheckoutService.customer?.email,
            address: {
              line1: billingAddress?.line1,
              line2: billingAddress?.line2,
              city: billingAddress?.city,
              state: billingAddress?.state,
              postal_code: billingAddress?.postal_code,
              country: billingAddress?.country,
            },
          },
        },
      },
      redirect: 'if_required',
    });

    return { error, intent: paymentIntent };
  } catch (error: any) {
    Sentry.captureException(error);
    return { error, intent: undefined };
  }
}

async function updateTeamPaymentMethod(paymentMethod?: PaymentMethod | string | null): Promise<void> {
  if (!paymentMethod) {
    return;
  }

  let paymentMethodId: string;
  if (typeof paymentMethod === 'string') {
    paymentMethodId = paymentMethod;
  } else {
    paymentMethodId = paymentMethod.id;
  }

  try {
    await axios.post(AppConfig.getAPIBaseUrl() + '/team/payment-method-change/update', {
      payment_method_id: paymentMethodId,
    });
  } catch (error) {
    Sentry.captureException(error);
  }
}

async function processPaymentIntentConfirmation(
  context: PaymentFlowContext,
  paymentIntent: PaymentIntent | undefined,
  error: any
): Promise<PaymentFlowResult> {
  if (error) {
    // Log out users who try to use lost or stolen cards
    if (error.decline_code && ['lost_card', 'stolen_card'].includes(error.decline_code)) {
      await lockUserAccount(error.decline_code);

      await Authenticator.logout();
    }

    if (error.type === 'card_error' || error.type === 'validation_error') {
      return {
        success: false,
        error: {
          // error.code indicates an error from stripe
          // error.decline_code takes precedence over error.code
          message_key: error.decline_code
            ? `checkout.form.stripe_errors.${error.decline_code}`
            : error.code
            ? `checkout.form.stripe_errors.${error.code}`
            : 'checkout.checkout_errors.unexpected_payment_processing_error',
          container: 'payment-element-container',
        },
      };
    }

    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-107',
      },
    };
  }

  if (!paymentIntent) {
    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-108',
      },
    };
  }

  const { id, status, payment_method } = paymentIntent;

  if (status === 'succeeded') {
    // in case of payment method exists, we need to update the team payment method
    // before webhook arrives and updates the team payment method to be in sync as soon as possible
    await updateTeamPaymentMethod(payment_method);

    await redirectToPaymentComplete(context);

    return { success: true };
  } else if (paymentIntent.status === 'requires_action') {
    Sentry.captureException(new Error('unexpected state from Stripe payment intent, breaking checkout flow'), {
      extra: { paymentIntentId: id },
    });

    return {
      success: false,
      error: {
        message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
        container: 'payment-element-container',
        error_code: 'ERR-109',
      },
    };
  }

  // Fallback for any other intent status
  return {
    success: false,
    error: {
      message_key: 'checkout.checkout_errors.unexpected_payment_processing_error',
      container: 'payment-element-container',
      error_code: 'ERR-110',
    },
  };
}
